import { useCallback, useEffect, useState } from 'react'

interface UseDataTableColumnsProps<T> {
  columns: T[]
  defaultVisibleColumns?: T[]
  defaultDisabledColumns?: T[]
}

interface UseDataTableColumnsReturn<T> {
  visibleColumns: T[]
  isColumnVisible: (column: T) => boolean
  changeColumnVisibility: (column: T) => void
  disabledColumns: T[]
}

export function useDataTableColumns<T>({
  columns,
  defaultVisibleColumns,
  defaultDisabledColumns = [],
}: UseDataTableColumnsProps<T>): UseDataTableColumnsReturn<T> {
  // Initialize with either provided visible columns or all non-disabled columns
  const [visibleColumns, setVisibleColumns] = useState<T[]>(
    () => defaultVisibleColumns ?? columns.filter((col) => !defaultDisabledColumns.includes(col))
  )

  const isColumnVisible = useCallback(
    (column: T) => {
      return visibleColumns.includes(column)
    },
    [visibleColumns]
  )

  const changeColumnVisibility = useCallback(
    (column: T) => {
      // Don't allow toggling disabled columns
      if (defaultDisabledColumns.includes(column)) {
        return
      }

      setVisibleColumns((prev) => {
        const isCurrentlyVisible = prev.includes(column)

        if (isCurrentlyVisible) {
          // Don't allow hiding if it would hide all columns
          if (prev.length <= 1) {
            return prev
          }
          return prev.filter((col) => col !== column)
        } else {
          return [...prev, column]
        }
      })
    },
    [defaultDisabledColumns]
  )

  return {
    visibleColumns,
    isColumnVisible,
    changeColumnVisibility,
    disabledColumns: defaultDisabledColumns,
  }
}
